<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-2">
      <h1 class="font-extrabold text-2xl">
        {{ t("executiveDashboard.title") }}
      </h1>
      <div class="text-light-gray text-sm">
        {{ t("executiveDashboard.subTitle") }}
      </div>
    </div>
    <div class="bg-white rounded-xl shadow-md p-4" v-if="!isLoading">
      <CustomDropDown
        :title="t('executiveDashboard.selectMonth')"
        :data="monthsByYear"
        dataKey="year"
        dataValue="year"
        subOptionsKey="months"
        subOptionKey="id"
        subOptionValue="month"
        v-model="selectedMonth"
        @update:modelValue="getDataByMonth"
      />
    </div>
    <div v-if="isLoading" class="flex h-[60vh] justify-center">
      <LoadingIcon :isLoading="isLoading" />
    </div>
    <div
      class="flex flex-col gap-4"
      v-if="!isLoading && executiveDashboardData"
    >
      <div class="grid grid-cols-4 gap-3 my-4">
        <InfoBox
          :title="t('executiveDashboard.activeEvents.title')"
          :value="executiveDashboardData!.activeEvents.value"
          backgroundColor="var(--color-light-blue)"
          textColor="var(--color-blue)"
          :trend="executiveDashboardData.activeEvents.trend"
          :change="
            executiveDashboardData.activeEvents.change +
            ' ' +
            t('executiveDashboard.activeEvents.subTitle')
          "
        >
          <IconManager name="Calendar" class="text-blue" />
        </InfoBox>

        <InfoBox
          :title="t('executiveDashboard.estimatedCost.title')"
          :value="executiveDashboardData!.estimatedCost.value + '€'"
          backgroundColor="var(--color-secondary)"
          textColor="var(--color-primary)"
          :trend="executiveDashboardData.estimatedCost.trend"
          :change="
            executiveDashboardData.estimatedCost.change +
            ' ' +
            t('executiveDashboard.estimatedCost.subTitle')
          "
        >
          <IconManager name="Euro" class="text-primary" />
        </InfoBox>

        <InfoBox
          :title="t('executiveDashboard.averageMargin.title')"
          :value="executiveDashboardData!.margin.value + '%'"
          backgroundColor="var(--color-light-cyan-green)"
          textColor="var(--color-cyan-green)"
          :trend="executiveDashboardData.margin.trend"
          :change="executiveDashboardData.margin.change?.toString()"
        >
          <IconManager name="Percentage" class="text-cyan-green" />
        </InfoBox>

        <InfoBox
          :title="t('executiveDashboard.occupation.title')"
          :value="executiveDashboardData!.occupation.value + '%'"
          backgroundColor="var(--color-light-purple)"
          textColor="var(--color-purple)"
          :trend="executiveDashboardData.occupation.trend"
          :change="
            executiveDashboardData.occupation.change +
            ' ' +
            t('executiveDashboard.occupation.subTitle')
          "
        >
          <IconManager name="BarChart" class="text-purple" />
        </InfoBox>
      </div>

      <div class="flex flex-row gap-4">
        <div
          class="flex flex-col bg-white rounded-xl shadow-md p-4 w-full h-[500px]"
        >
          <div class="flex flex-row w-full justify-between">
            <span class="font-extrabold"
              >{{ t("executiveDashboard.donutChartTitle") }}
            </span>
          </div>
          <DonutChart
            :data="executiveDashboardData!.donutChart"
            labelPosition="outside"
          />
        </div>

        <div class="flex flex-col bg-white rounded-xl shadow-md p-4 w-full">
          <div class="flex flex-row w-full justify-between">
            <span class="font-extrabold">{{
              t("executiveDashboard.heatChart.title")
            }}</span>
            <span class="text-light-gray">
              {{ t("executiveDashboard.heatChart.subTitle") }}
            </span>
          </div>
          <HeatMap
            :xLabels="executiveDashboardData!.heatChart.xLabel"
            :yLabels="executiveDashboardData!.heatChart.yLabel"
            :data="executiveDashboardData!.heatChart.rows"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from "@/core/composables/use-toast";
import IconManager from "@/core/IconManager.vue";
import InfoBox from "@/core/components/InfoBox.vue";
import { useI18n } from "vue-i18n";
import HeatMap from "@/core/components/charts/HeatMap.vue";
import DonutChart from "@/core/components/charts/DonutChart.vue";
import { executiveDashboardRemoteDataSource } from "@/core/models/global-variables";
import { onMounted, ref } from "vue";
import { ExecutiveDashboard } from "../models/executive-dashboard";
import CustomDropDown from "@/core/components/CustomDropDown.vue";
import LoadingIcon from "@/core/components/LoadingIcon.vue";
import { format } from "date-fns";

const { t } = useI18n();
const { showError } = useToast();
const executiveDashboardData = ref<ExecutiveDashboard | null>(null);
const isLoading = ref<boolean>(true);
const { locale } = useI18n();
const months = getMonthNames(locale.value);

const monthsByYear = [
  { year: new Date().getFullYear(), months: months },
  { year: new Date().getFullYear() + 1, months: months },
];

const selectedMonth = ref<{
  option: { year: number; months: any[] };
  subOption: { id: number; month: string };
}>({
  option: monthsByYear[0],
  subOption: {
    id: new Date().getMonth(),
    month: months[new Date().getMonth()].month,
  },
});

onMounted(() => {
  loadInitialData();
});

async function loadInitialData() {
  await getDataByMonth();
}

async function getDataByMonth() {
  isLoading.value = true;
  try {
    const response =
      await executiveDashboardRemoteDataSource.getExecutiveDashboardData({
        date: format(
          new Date(
            selectedMonth.value.option.year,
            selectedMonth.value.subOption.id,
            1,
          ),
          "yyyy-MM-dd'T'HH:mm:ss",
        ),
      });
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    executiveDashboardData.value = response.data;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoading.value = false;
  }
}

function getMonthNames(locale: string): { id: number; month: string }[] {
  const formatter = new Intl.DateTimeFormat(locale, { month: "long" });
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 12 }, (_, i) => {
    const rawName = formatter.format(new Date(currentYear, i, 1));
    const capitalized = rawName.charAt(0).toUpperCase() + rawName.slice(1);
    return { id: i, month: capitalized };
  });
}
</script>
